import { cn } from "@/lib/utils";
import { ButtonHTMLAttributes, ReactNode } from "react";

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** The icon node, sized by the caller (typically size-4 / size-5). */
  icon: ReactNode;
  /** Corner treatment: "square" = rounded-[10px] (default), "round" = rounded-full. */
  shape?: "square" | "round";
  /**
   * Render as a non-interactive element (e.g. a decorative glyph nested
   * inside another button, where a nested <button> would be invalid).
   */
  as?: "button" | "span";
};

/**
 * The 30px dark icon button (square CloseButton/BackButton family, round
 * chemistry variant): one container, callers pass the icon. Named wrappers
 * with a fixed icon + label live in SpecificButtons.
 */
export const IconButton = ({
  icon,
  shape = "square",
  as = "button",
  className,
  ...props
}: IconButtonProps) => {
  const Comp = as as "button";
  const typeProp = as === "button" ? { type: "button" as const } : {};
  return (
    <Comp
      {...typeProp}
      className={cn(
        "flex size-[30px] shrink-0 items-center justify-center",
        shape === "round" ? "rounded-full" : "rounded-[10px]",
        "bg-neutral-700 border border-neutral-600",
        "hover:bg-neutral-600 transition-colors",
        className,
      )}
      {...props}
    >
      {icon}
    </Comp>
  );
};
